import { uuid } from "./ids.js";
import { attachmentKey, rankBetween } from "@gjwtrack/domain";
import { compileComparator, compilePredicate, compileQuery, indexSchema } from "@gjwtrack/query-language";
import { ApiError } from "./http.js";
import { LiveSocket } from "./socket.js";
import { initialState, recomputeDisplay, Store } from "./store.js";
import { applyViewMessage, localAdjust, newView } from "./views.js";
const MAX_ERRORS = 5;
/**
 * The client core: one store, one socket, one HTTP client. Views mirror
 * server windows; edits are applied optimistically and confirmed by the
 * command response or by the echoed event, whichever lands first (M3-PLAN).
 */
export class GjwClient {
    store = new Store(initialState());
    http;
    socket;
    newMutationId;
    log;
    compiled = new Map();
    errorSeq = 0;
    commentLoads = new Map();
    activityLoads = new Map();
    linkLoads = new Map();
    attachmentLoads = new Map();
    /** Messages held back for frozen (dragging) cards, in arrival order. */
    frozenQueue = [];
    constructor(opts) {
        this.http = opts.http;
        this.log = opts.log ?? (() => { });
        this.newMutationId = opts.newMutationId ?? (() => uuid());
        this.socket = new LiveSocket({
            createSocket: opts.createSocket,
            onMessage: (m) => this.handleMessage(m),
            onStatus: (s) => this.onSocketStatus(s),
            onReconnect: () => this.onReconnect(),
            log: this.log,
            ...opts.socket,
        });
    }
    get state() {
        return this.store.get();
    }
    // ----------------------------------------------------------------- lifecycle
    /** Load `/api/meta`, then open the socket. Throws ApiError(401) when not logged in. */
    async start() {
        const meta = await this.http.meta();
        this.applyMeta(meta);
        this.socket.start();
        return meta.me;
    }
    /** Merge my UI settings (issue list columns, view mode): the store updates at once, the server keeps them for other browsers. */
    async setUserSettings(patch) {
        this.store.set((s) => (s.me ? { ...s, me: { ...s.me, settings: { ...s.me.settings, ...patch } } } : s));
        try {
            const settings = await this.http.command("setUserSettings", patch, this.newMutationId());
            this.store.set((s) => (s.me ? { ...s, me: { ...s.me, settings } } : s));
        }
        catch (e) {
            this.pushError("settings", e.message);
        }
    }
    async reloadMeta() {
        this.applyMeta(await this.http.meta());
    }
    stop() {
        this.socket.stop();
    }
    applyMeta(meta) {
        const schema = indexSchema(meta.schema);
        this.compiled.clear();
        this.store.set((s) => ({ ...s, me: meta.me, schema, groups: meta.groups }));
    }
    onSocketStatus(status) {
        this.store.set((s) => {
            const views = { ...s.views };
            const boards = { ...s.boards };
            if (status !== "open") {
                for (const v of Object.values(views))
                    if (v.status === "live")
                        views[v.sub] = { ...v, status: "stale" };
                for (const [id, b] of Object.entries(boards))
                    if (b.status === "live")
                        boards[Number(id)] = { ...b, status: "stale" };
            }
            return { ...s, connection: status, views, boards };
        });
    }
    onReconnect() {
        // Every view is re-subscribed by the socket; each gets a fresh snapshot.
        this.store.set((s) => {
            const views = { ...s.views };
            for (const v of Object.values(views))
                views[v.sub] = { ...v, status: "loading" };
            return { ...s, views };
        });
    }
    // ----------------------------------------------------------------- views
    openView(sub, query, limit = 50) {
        this.compiled.delete(sub);
        this.store.set((s) => ({ ...s, views: { ...s.views, [sub]: newView(sub, query, limit) } }));
        this.socket.subscribe(sub, query, limit);
    }
    closeView(sub) {
        this.compiled.delete(sub);
        this.socket.unsubscribe(sub);
        this.store.set((s) => {
            if (!(sub in s.views))
                return s;
            const views = { ...s.views };
            delete views[sub];
            return { ...s, views };
        });
    }
    /** Server messages, also fed directly by tests. */
    handleMessage(msg) {
        if (msg.t === "hello" || msg.t === "pong")
            return;
        if (msg.t === "article") {
            this.store.set((s) => ({ ...s, articleTick: { seq: (s.articleTick?.seq ?? 0) + 1, kind: msg.kind, id: msg.id, projectId: msg.projectId, version: msg.version, mutationId: msg.mutationId ?? null } }));
            return;
        }
        if (msg.t === "notification") {
            this.store.set((s) => ({
                ...s,
                notifications: { ...s.notifications, items: [msg.notification, ...s.notifications.items.filter((n) => n.id !== msg.notification.id)].slice(0, 200), unread: s.notifications.unread + (msg.notification.readAt === null ? 1 : 0) },
            }));
            return;
        }
        const sub = msg.sub;
        if (sub === undefined) {
            if (msg.t === "error")
                this.pushError(msg.code, msg.message);
            return;
        }
        // A card being dragged keeps its screen state until the drop (BLUEPRINT §11).
        const frozenId = msg.t === "enter" || msg.t === "update" ? msg.issue.id : msg.t === "board.position" ? msg.position.issueId : null;
        if (frozenId !== null && this.state.frozen.includes(frozenId)) {
            this.frozenQueue.push(msg);
            return;
        }
        if (msg.t === "board.snapshot" || msg.t === "board.position" || (msg.t === "resync" && msg.reason === "board") || (msg.t === "error" && sub.startsWith("board:"))) {
            this.handleBoardMessage(sub, msg);
            return;
        }
        const view = this.state.views[sub];
        if (!view)
            return; // late message for a closed view
        const { view: next, issues } = applyViewMessage(view, msg);
        const touched = [];
        this.store.set((s) => {
            let out = s;
            const serverIssues = { ...s.issues };
            let pending = s.pending;
            for (const issue of issues) {
                const cur = serverIssues[issue.id];
                if (!cur || issue.version >= cur.version) {
                    serverIssues[issue.id] = issue;
                    touched.push(issue.id);
                }
            }
            if ((msg.t === "enter" || msg.t === "update") && msg.mutationId !== undefined) {
                const mid = msg.mutationId;
                if (pending.some((p) => p.mutationId === mid)) {
                    pending = pending.filter((p) => p.mutationId !== mid);
                    touched.push(msg.issue.id);
                }
            }
            out = { ...out, issues: serverIssues, pending, views: { ...out.views, [sub]: next } };
            return recomputeDisplay(out, touched);
        });
        if (msg.t === "resync")
            this.socket.subscribe(sub, view.query, view.limit);
        for (const id of touched) {
            this.refreshCommentsIfOpen(id);
            this.refreshActivityIfOpen(id);
            this.refreshLinksIfOpen(id);
            this.refreshAttachmentsIfOpen(id);
        }
    }
    // ----------------------------------------------------------------- boards
    boardSub = (boardId) => `board:${boardId}`;
    handleBoardMessage(sub, msg) {
        const boardId = Number(sub.slice("board:".length));
        const cur = this.state.boards[boardId];
        if (!cur)
            return;
        if (msg.t === "board.snapshot") {
            const positions = {};
            for (const p of msg.positions)
                positions[p.issueId] = p;
            this.store.set((s) => ({ ...s, boards: { ...s.boards, [boardId]: { ...cur, positions, status: "live", error: null, version: msg.version } } }));
        }
        else if (msg.t === "board.position") {
            this.store.set((s) => {
                const b = s.boards[boardId];
                if (!b)
                    return s;
                return { ...s, boards: { ...s.boards, [boardId]: { ...b, positions: { ...b.positions, [msg.position.issueId]: msg.position }, version: msg.version } } };
            });
        }
        else if (msg.t === "resync") {
            void this.reloadBoard(boardId);
        }
        else if (msg.t === "error") {
            this.store.set((s) => ({ ...s, boards: { ...s.boards, [boardId]: { ...cur, status: "error", error: msg.message } } }));
        }
    }
    /** Load a board's settings and follow its positions. Cards come from a separate `openView` on `board.query`. */
    async openBoard(boardId) {
        this.store.set((s) => ({
            ...s,
            boards: { ...s.boards, [boardId]: s.boards[boardId] ?? { board: null, sprints: [], positions: {}, status: "loading", error: null, version: 0 } },
        }));
        await this.reloadBoard(boardId);
    }
    async reloadBoard(boardId) {
        try {
            const { board, sprints } = await this.http.board(boardId);
            this.store.set((s) => {
                const cur = s.boards[boardId];
                if (!cur)
                    return s;
                return { ...s, boards: { ...s.boards, [boardId]: { ...cur, board, sprints, status: "loading" } } };
            });
            this.socket.subscribeBoard(this.boardSub(boardId), boardId);
        }
        catch (e) {
            this.store.set((s) => {
                const cur = s.boards[boardId];
                return cur ? { ...s, boards: { ...s.boards, [boardId]: { ...cur, status: "error", error: e.message } } } : s;
            });
        }
    }
    closeBoard(boardId) {
        this.socket.unsubscribe(this.boardSub(boardId));
        this.store.set((s) => {
            if (!(boardId in s.boards))
                return s;
            const boards = { ...s.boards };
            delete boards[boardId];
            return { ...s, boards };
        });
    }
    /**
     * Optimistic drop: same rule as the server (trust one neighbour, take the
     * other from the current positions), so the card lands where the user put
     * it before the round trip; the echoed `board.position` then confirms.
     */
    moveCard(boardId, issueId, opts) {
        const b = this.state.boards[boardId];
        if (b) {
            const sprintId = opts.sprintId === undefined ? (b.positions[issueId]?.sprintId ?? null) : opts.sprintId;
            const others = Object.values(b.positions).filter((p) => p.issueId !== issueId && p.sprintId === sprintId);
            const rankOf = (id) => (id === null || id === undefined ? null : (b.positions[id]?.rank ?? null));
            let lo;
            let hi;
            if (opts.afterIssueId !== null && opts.afterIssueId !== undefined) {
                lo = rankOf(opts.afterIssueId);
                hi = others.filter((p) => lo !== null && p.rank > lo).map((p) => p.rank).sort()[0] ?? null;
            }
            else if (opts.beforeIssueId !== null && opts.beforeIssueId !== undefined) {
                hi = rankOf(opts.beforeIssueId);
                lo = others.filter((p) => hi !== null && p.rank < hi).map((p) => p.rank).sort().at(-1) ?? null;
            }
            else {
                lo = others.map((p) => p.rank).sort().at(-1) ?? null;
                hi = null;
            }
            try {
                const rank = rankBetween(lo, hi);
                this.store.set((s) => {
                    const cur = s.boards[boardId];
                    return cur ? { ...s, boards: { ...s.boards, [boardId]: { ...cur, positions: { ...cur.positions, [issueId]: { issueId, rank, sprintId } } } } } : s;
                });
            }
            catch (e) {
                this.log(`local rank failed: ${e.message}`);
            }
        }
        return this.http
            .command("moveCard", { boardId, issueId, afterIssueId: opts.afterIssueId ?? null, beforeIssueId: opts.beforeIssueId ?? null, ...(opts.sprintId !== undefined ? { sprintId: opts.sprintId } : {}) }, this.newMutationId())
            .then(() => undefined)
            .catch(async (e) => {
            this.pushError(e instanceof ApiError ? e.code : "network", e.message);
            await this.reloadPositions(boardId);
        });
    }
    async setSprint(boardId, issueIds, sprintId) {
        try {
            await this.http.command("setSprint", { boardId, issueIds, sprintId }, this.newMutationId());
        }
        catch (e) {
            this.pushError(e instanceof ApiError ? e.code : "network", e.message);
            await this.reloadPositions(boardId);
        }
    }
    async reloadPositions(boardId) {
        try {
            const list = await this.http.positions(boardId);
            const positions = {};
            for (const p of list)
                positions[p.issueId] = p;
            this.store.set((s) => {
                const cur = s.boards[boardId];
                return cur ? { ...s, boards: { ...s.boards, [boardId]: { ...cur, positions } } } : s;
            });
        }
        catch (e) {
            this.log(`reloadPositions failed: ${e.message}`);
        }
    }
    /** Hold incoming changes for a card while it is being dragged. */
    freeze(issueId) {
        this.store.set((s) => (s.frozen.includes(issueId) ? s : { ...s, frozen: [...s.frozen, issueId] }));
    }
    /** Release a card and apply what arrived meanwhile, in order. */
    unfreeze(issueId) {
        this.store.set((s) => ({ ...s, frozen: s.frozen.filter((id) => id !== issueId) }));
        const mine = this.frozenQueue.filter((m) => (m.t === "enter" || m.t === "update" ? m.issue.id === issueId : m.t === "board.position" && m.position.issueId === issueId));
        this.frozenQueue = this.frozenQueue.filter((m) => !mine.includes(m));
        for (const m of mine)
            this.handleMessage(m);
    }
    // ----------------------------------------------------------------- attachments
    loadAttachments(target) {
        const key = attachmentKey(target);
        const inflight = this.attachmentLoads.get(key);
        if (inflight)
            return inflight;
        this.store.set((s) => ({ ...s, attachments: { ...s.attachments, [key]: { status: "loading", items: s.attachments[key]?.items ?? [] } } }));
        const p = this.http
            .attachments(target)
            .then((items) => this.store.set((s) => ({ ...s, attachments: { ...s.attachments, [key]: { status: "ready", items } } })))
            .catch((e) => this.pushError("attachments", e.message))
            .finally(() => this.attachmentLoads.delete(key));
        this.attachmentLoads.set(key, p);
        return p;
    }
    forgetAttachments(target) {
        const key = attachmentKey(target);
        this.store.set((s) => {
            if (!(key in s.attachments))
                return s;
            const attachments = { ...s.attachments };
            delete attachments[key];
            return { ...s, attachments };
        });
    }
    refreshAttachmentsIfOpen(issueId) {
        if (attachmentKey({ issueId }) in this.state.attachments)
            void this.loadAttachments({ issueId });
    }
    async uploadAttachment(target, file, name) {
        try {
            const att = await this.http.upload(target, file, name);
            await this.loadAttachments(target);
            return att;
        }
        catch (e) {
            this.pushError(e instanceof ApiError ? e.code : "network", e.message);
            throw e;
        }
    }
    async deleteAttachment(target, attachmentId) {
        const key = attachmentKey(target);
        this.store.set((s) => {
            const cur = s.attachments[key];
            return cur ? { ...s, attachments: { ...s.attachments, [key]: { ...cur, items: cur.items.filter((a) => a.id !== attachmentId) } } } : s;
        });
        try {
            await this.http.command("deleteAttachment", { attachmentId }, this.newMutationId());
        }
        catch (e) {
            this.pushError(e instanceof ApiError ? e.code : "network", e.message);
            await this.loadAttachments(target);
        }
    }
    // ----------------------------------------------------------------- issues
    /** The issue as rendered (server state + pending patches), fetching it when unknown. */
    async ensureIssue(id) {
        const known = this.state.display[id];
        if (known)
            return known;
        try {
            const issue = await this.http.issue(id);
            this.absorb(issue);
            return this.state.display[id] ?? null;
        }
        catch (e) {
            if (e instanceof ApiError && e.status === 404)
                return null;
            throw e;
        }
    }
    /** Take a server payload as truth if it is at least as new as what we hold. */
    absorb(issue, key) {
        this.store.set((s) => {
            const cur = s.issues[issue.id];
            const keyIndex = key ? { ...s.keyIndex, [key.toUpperCase()]: issue.id } : s.keyIndex;
            if (cur && issue.version < cur.version)
                return keyIndex === s.keyIndex ? s : { ...s, keyIndex };
            return recomputeDisplay({ ...s, issues: { ...s.issues, [issue.id]: issue }, keyIndex }, [issue.id]);
        });
        this.adjustViews(issue.id);
    }
    /** Thumbs up / take it back. The server's `issue.updated` (new `voters`) updates the issue in the store. */
    async vote(issueId, vote) {
        try {
            await this.http.command("voteIssue", { issueId, vote }, uuid());
        }
        catch (e) {
            this.pushError(e instanceof ApiError ? e.code : "network", e.message);
        }
    }
    /** Put issues fetched over HTTP (a static list page beyond the live window) into the store so rows render from `display`. */
    ingest(issues) {
        for (const issue of issues)
            this.absorb(issue);
    }
    /**
     * Fetch issues mentioned by key (e.g. in a description) so the renderer can
     * strike through resolved ones. Already-known keys are skipped.
     */
    async resolveKeys(keys, keyOf) {
        const wanted = [...new Set(keys.map((k) => k.toUpperCase()))].filter((k) => !(k in this.state.keyIndex));
        if (wanted.length === 0)
            return;
        // Mark as looked up even if absent, so unknown keys are not refetched on every render.
        this.store.set((s) => ({ ...s, keyIndex: { ...s.keyIndex, ...Object.fromEntries(wanted.map((k) => [k, s.keyIndex[k] ?? -1])) } }));
        let issues;
        try {
            issues = await this.http.byKeys(wanted);
        }
        catch (e) {
            this.log(`resolveKeys failed: ${e.message}`);
            return;
        }
        for (const issue of issues)
            this.absorb(issue, keyOf(issue));
    }
    async createIssue(input) {
        const issue = await this.http.command("createIssue", input, this.newMutationId());
        this.absorb(issue);
        return issue;
    }
    /** Optimistic: shows immediately, sends in order per issue, rolls back on rejection. */
    updateIssue(issueId, patch) {
        const m = { mutationId: this.newMutationId(), issueId, patch, status: "queued" };
        this.store.set((s) => recomputeDisplay({ ...s, pending: [...s.pending, m] }, [issueId]));
        this.adjustViews(issueId);
        void this.pump(issueId);
        return m.mutationId;
    }
    /** Resolves when no mutation for this issue is outstanding. */
    async settled(issueId) {
        while (this.state.pending.some((p) => issueId === undefined || p.issueId === issueId)) {
            await new Promise((r) => {
                const un = this.store.subscribe(() => {
                    un();
                    r();
                });
            });
        }
    }
    async pump(issueId) {
        const s = this.state;
        if (s.pending.some((p) => p.issueId === issueId && p.status === "sent"))
            return;
        const next = s.pending.find((p) => p.issueId === issueId && p.status === "queued");
        if (!next)
            return;
        const server = s.issues[issueId];
        if (!server) {
            this.dropPending(next.mutationId, "unknown_issue", "issue not loaded");
            return;
        }
        this.store.set((st) => ({ ...st, pending: st.pending.map((p) => (p.mutationId === next.mutationId ? { ...p, status: "sent" } : p)) }));
        try {
            const result = await this.http.command("updateIssue", { issueId, expectedVersion: server.version, patch: next.patch }, next.mutationId);
            this.settle(next.mutationId, result);
        }
        catch (e) {
            if (e instanceof ApiError) {
                const current = e.detail.current;
                if (e.status === 409 && current)
                    this.absorb(current);
                this.dropPending(next.mutationId, e.code, e.message);
            }
            else {
                this.dropPending(next.mutationId, "network", e.message);
            }
        }
        void this.pump(issueId);
    }
    settle(mutationId, result) {
        this.store.set((s) => {
            const cur = s.issues[result.id];
            const issues = !cur || result.version >= cur.version ? { ...s.issues, [result.id]: result } : s.issues;
            return recomputeDisplay({ ...s, issues, pending: s.pending.filter((p) => p.mutationId !== mutationId) }, [result.id]);
        });
        this.adjustViews(result.id);
    }
    dropPending(mutationId, code, message) {
        const m = this.state.pending.find((p) => p.mutationId === mutationId);
        if (!m)
            return;
        this.store.set((s) => recomputeDisplay({ ...s, pending: s.pending.filter((p) => p.mutationId !== mutationId) }, [m.issueId]));
        this.adjustViews(m.issueId);
        this.pushError(code, message);
    }
    /** Re-estimate every view's window for one issue with the local predicate and comparator. */
    adjustViews(issueId) {
        const s = this.state;
        const issue = s.display[issueId];
        if (!issue)
            return;
        let views = s.views;
        for (const v of Object.values(s.views)) {
            const c = this.compiledFor(v);
            if (!c)
                continue;
            const cmp = (a, b) => {
                const ia = s.display[a];
                const ib = s.display[b];
                return ia && ib ? c.comparator(ia, ib) : 0;
            };
            const next = localAdjust(v, issueId, c.predicate(issue), cmp);
            if (next !== v)
                views = { ...views, [v.sub]: next };
        }
        if (views !== s.views)
            this.store.set((st) => ({ ...st, views }));
    }
    compiledFor(v) {
        const { schema, me } = this.state;
        if (!schema || !me)
            return null;
        const hit = this.compiled.get(v.sub);
        if (hit !== undefined && (hit === null || (hit.query === v.query && hit.schema === schema)))
            return hit;
        try {
            const resolved = compileQuery(v.query, schema.bind(me.id));
            const c = { query: v.query, schema, predicate: compilePredicate(resolved), comparator: compileComparator(resolved, schema.bind(me.id)) };
            this.compiled.set(v.sub, c);
            return c;
        }
        catch (e) {
            this.log(`local compile failed for ${JSON.stringify(v.query)}: ${e.message}`);
            this.compiled.set(v.sub, null);
            return null;
        }
    }
    // ----------------------------------------------------------------- comments
    loadComments(issueId) {
        const inflight = this.commentLoads.get(issueId);
        if (inflight)
            return inflight;
        this.store.set((s) => ({
            ...s,
            comments: { ...s.comments, [issueId]: { status: "loading", items: s.comments[issueId]?.items ?? [] } },
        }));
        const p = this.http
            .comments(issueId)
            .then((items) => {
            this.store.set((s) => ({ ...s, comments: { ...s.comments, [issueId]: { status: "ready", items } } }));
        })
            .catch((e) => this.pushError("comments", e.message))
            .finally(() => this.commentLoads.delete(issueId));
        this.commentLoads.set(issueId, p);
        return p;
    }
    forgetComments(issueId) {
        this.store.set((s) => {
            if (!(issueId in s.comments))
                return s;
            const comments = { ...s.comments };
            delete comments[issueId];
            return { ...s, comments };
        });
    }
    refreshCommentsIfOpen(issueId) {
        if (issueId in this.state.comments)
            void this.loadComments(issueId);
    }
    // ----------------------------------------------------------------- activity
    /** Activity list of a detail page; refetched after every event on the issue, like comments. */
    loadActivity(issueId) {
        const inflight = this.activityLoads.get(issueId);
        if (inflight)
            return inflight;
        this.store.set((s) => ({
            ...s,
            activity: { ...s.activity, [issueId]: { status: "loading", items: s.activity[issueId]?.items ?? [] } },
        }));
        const p = this.http
            .activity(issueId)
            .then((items) => {
            this.store.set((s) => ({ ...s, activity: { ...s.activity, [issueId]: { status: "ready", items } } }));
        })
            .catch((e) => this.pushError("activity", e.message))
            .finally(() => this.activityLoads.delete(issueId));
        this.activityLoads.set(issueId, p);
        return p;
    }
    forgetActivity(issueId) {
        this.store.set((s) => {
            if (!(issueId in s.activity))
                return s;
            const activity = { ...s.activity };
            delete activity[issueId];
            return { ...s, activity };
        });
    }
    refreshActivityIfOpen(issueId) {
        if (issueId in this.state.activity)
            void this.loadActivity(issueId);
    }
    async editComment(issueId, commentId, text) {
        this.store.set((s) => {
            const cur = s.comments[issueId];
            return cur ? { ...s, comments: { ...s.comments, [issueId]: { ...cur, items: cur.items.map((c) => (c.id === commentId ? { ...c, text } : c)) } } } : s;
        });
        try {
            await this.http.command("updateComment", { commentId, text }, this.newMutationId());
        }
        catch (e) {
            this.pushError(e instanceof ApiError ? e.code : "network", e.message);
            await this.loadComments(issueId);
            throw e;
        }
    }
    async deleteComment(issueId, commentId) {
        this.store.set((s) => {
            const cur = s.comments[issueId];
            return cur ? { ...s, comments: { ...s.comments, [issueId]: { ...cur, items: cur.items.filter((c) => c.id !== commentId) } } } : s;
        });
        try {
            await this.http.command("deleteComment", { commentId }, this.newMutationId());
        }
        catch (e) {
            this.pushError(e instanceof ApiError ? e.code : "network", e.message);
            await this.loadComments(issueId);
            throw e;
        }
    }
    async addComment(issueId, text) {
        const me = this.state.me;
        if (!me)
            throw new Error("not started");
        const issue = this.state.display[issueId];
        const optimistic = {
            id: -Date.now(),
            issueId,
            projectId: issue?.projectId ?? 0,
            authorId: me.id,
            text,
            visibility: null,
            version: -1,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            deletedAt: null,
        };
        this.store.set((s) => {
            const cur = s.comments[issueId] ?? { status: "ready", items: [] };
            return { ...s, comments: { ...s.comments, [issueId]: { ...cur, items: [...cur.items, optimistic] } } };
        });
        try {
            await this.http.command("addComment", { issueId, text, visibility: null }, this.newMutationId());
            await this.loadComments(issueId);
        }
        catch (e) {
            this.store.set((s) => {
                const cur = s.comments[issueId];
                if (!cur)
                    return s;
                return { ...s, comments: { ...s.comments, [issueId]: { ...cur, items: cur.items.filter((c) => c.id !== optimistic.id) } } };
            });
            this.pushError(e instanceof ApiError ? e.code : "network", e.message);
            throw e;
        }
    }
    // ----------------------------------------------------------------- links
    loadLinks(issueId) {
        const inflight = this.linkLoads.get(issueId);
        if (inflight)
            return inflight;
        this.store.set((s) => ({ ...s, links: { ...s.links, [issueId]: { status: "loading", items: s.links[issueId]?.items ?? [] } } }));
        const p = this.http
            .links(issueId)
            .then((items) => {
            this.store.set((s) => ({ ...s, links: { ...s.links, [issueId]: { status: "ready", items } } }));
            for (const l of items)
                this.absorb(l.issue);
        })
            .catch((e) => this.pushError("links", e.message))
            .finally(() => this.linkLoads.delete(issueId));
        this.linkLoads.set(issueId, p);
        return p;
    }
    forgetLinks(issueId) {
        this.store.set((s) => {
            if (!(issueId in s.links))
                return s;
            const links = { ...s.links };
            delete links[issueId];
            return { ...s, links };
        });
    }
    refreshLinksIfOpen(issueId) {
        if (issueId in this.state.links)
            void this.loadLinks(issueId);
    }
    async linkIssues(fromId, toId, type) {
        try {
            await this.http.command("linkIssues", { fromId, toId, type }, this.newMutationId());
        }
        catch (e) {
            this.pushError(e instanceof ApiError ? e.code : "network", e.message);
            throw e;
        }
        await this.loadLinks(fromId);
    }
    async unlinkIssues(issueId, linkId) {
        // Optimistic: drop the row now; the touch event reloads the truth.
        this.store.set((s) => {
            const cur = s.links[issueId];
            return cur ? { ...s, links: { ...s.links, [issueId]: { ...cur, items: cur.items.filter((l) => l.id !== linkId) } } } : s;
        });
        try {
            await this.http.command("unlinkIssues", { linkId }, this.newMutationId());
        }
        catch (e) {
            this.pushError(e instanceof ApiError ? e.code : "network", e.message);
            await this.loadLinks(issueId);
            throw e;
        }
    }
    // ----------------------------------------------------------------- watchers
    async loadWatchers(issueId) {
        this.store.set((s) => ({ ...s, watchers: { ...s.watchers, [issueId]: s.watchers[issueId] ?? { status: "loading", userIds: [], me: false } } }));
        try {
            const w = await this.http.watchers(issueId);
            this.store.set((s) => ({ ...s, watchers: { ...s.watchers, [issueId]: { status: "ready", ...w } } }));
        }
        catch (e) {
            this.pushError("watchers", e.message);
        }
    }
    /** Star / unstar, shown at once; reverted if the server says no. */
    async watch(issueId, watch) {
        const me = this.state.me;
        if (!me)
            return;
        const apply = (on) => this.store.set((s) => {
            const cur = s.watchers[issueId] ?? { status: "ready", userIds: [], me: false };
            const userIds = on ? [...new Set([...cur.userIds, me.id])] : cur.userIds.filter((u) => u !== me.id);
            return { ...s, watchers: { ...s.watchers, [issueId]: { ...cur, userIds, me: on } } };
        });
        apply(watch);
        try {
            await this.http.command("watchIssue", { issueId, watch }, this.newMutationId());
        }
        catch (e) {
            apply(!watch);
            this.pushError(e instanceof ApiError ? e.code : "network", e.message);
        }
    }
    // ----------------------------------------------------------------- tags
    /**
     * Create a tag and return its id once the schema snapshot includes it. The
     * server's snapshot reloads from the event stream a few tens of ms after the
     * command commits, so `/api/meta` is polled briefly rather than read once.
     */
    async createTag(name, color = null) {
        const { id } = await this.http.command("createTag", { name, color }, this.newMutationId());
        for (let attempt = 0; attempt < 20; attempt++) {
            await this.reloadMeta();
            if (this.state.schema?.tagsById.has(id))
                break;
            await new Promise((r) => setTimeout(r, 100));
        }
        return id;
    }
    // ----------------------------------------------------------------- notifications, saved searches, drafts
    async loadNotifications() {
        try {
            const r = await this.http.notifications({ limit: 50 });
            this.store.set((s) => ({ ...s, notifications: { items: r.notifications, unread: r.unread, loaded: true } }));
        }
        catch (e) {
            this.log(`notifications: ${e.message}`);
        }
    }
    /** Mark read locally at once, then tell the server. */
    async markRead(ids) {
        const now = Date.now();
        this.store.set((s) => {
            const items = s.notifications.items.map((n) => (n.readAt === null && (ids === "all" || ids.includes(n.id)) ? { ...n, readAt: now } : n));
            const unread = ids === "all" ? 0 : Math.max(0, s.notifications.unread - s.notifications.items.filter((n) => n.readAt === null && ids.includes(n.id)).length);
            return { ...s, notifications: { ...s.notifications, items, unread } };
        });
        try {
            const r = await this.http.command("markNotificationsRead", ids === "all" ? { all: true } : { ids }, this.newMutationId());
            this.store.set((s) => ({ ...s, notifications: { ...s.notifications, unread: r.unread } }));
        }
        catch (e) {
            this.pushError(e instanceof ApiError ? e.code : "network", e.message);
        }
    }
    async loadSavedSearches() {
        try {
            const savedSearches = await this.http.savedSearches();
            this.store.set((s) => ({ ...s, savedSearches }));
        }
        catch (e) {
            this.log(`saved searches: ${e.message}`);
        }
    }
    async loadDrafts() {
        try {
            const drafts = await this.http.drafts();
            this.store.set((s) => ({ ...s, drafts }));
        }
        catch (e) {
            this.log(`drafts: ${e.message}`);
        }
    }
    // ----------------------------------------------------------------- errors
    pushError(code, message) {
        const err = { id: ++this.errorSeq, at: Date.now(), code, message };
        this.store.set((s) => ({ ...s, errors: [...s.errors, err].slice(-MAX_ERRORS) }));
    }
    dismissError(id) {
        this.store.set((s) => ({ ...s, errors: s.errors.filter((e) => e.id !== id) }));
    }
}
//# sourceMappingURL=client.js.map